// OMEGA ERP — Multi-Language Support (Bangla / English)
// Usage: import { useI18n } from "@/lib/erp/i18n"
// const { t, lang, setLang } = useI18n();
// <h1>{t("Procurement")}</h1> → "Procurement" or "প্রকিউরমেন্ট"

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export type Language = "en" | "bn";

// Translation dictionary — key → { en, bn }
const translations: Record<string, { en: string; bn: string }> = {
  // ===== Common =====
  "Executive Control Tower": { en: "Executive Control Tower", bn: "এক্সিকিউটিভ কন্ট্রোল টাওয়ার" },
  "Sign In": { en: "Sign In", bn: "সাইন ইন" },
  "Sign Out": { en: "Sign Out", bn: "সাইন আউট" },
  "Search": { en: "Search", bn: "অনুসন্ধান" },
  "New": { en: "New", bn: "নতুন" },
  "Edit": { en: "Edit", bn: "সম্পাদনা" },
  "Delete": { en: "Delete", bn: "মুছুন" },
  "Save": { en: "Save", bn: "সংরক্ষণ" },
  "Cancel": { en: "Cancel", bn: "বাতিল" },
  "Print": { en: "Print", bn: "প্রিন্ট" },
  "Export": { en: "Export", bn: "এক্সপোর্ট" },
  "Import": { en: "Import", bn: "ইম্পোর্ট" },
  "Filter": { en: "Filter", bn: "ফিল্টার" },
  "Actions": { en: "Actions", bn: "অ্যাকশন" },
  "Status": { en: "Status", bn: "স্ট্যাটাস" },
  "Date": { en: "Date", bn: "তারিখ" },
  "Total": { en: "Total", bn: "মোট" },
  "Value": { en: "Value", bn: "মূল্য" },
  "Quantity": { en: "Quantity", bn: "পরিমাণ" },
  "Amount": { en: "Amount", bn: "পরিমাণ" },
  "Back": { en: "Back", bn: "পেছনে" },
  "Close": { en: "Close", bn: "বন্ধ" },
  "Submit": { en: "Submit", bn: "জমা দিন" },
  "Confirm": { en: "Confirm", bn: "নিশ্চিত করুন" },
  "View": { en: "View", bn: "দেখুন" },
  "Loading": { en: "Loading...", bn: "লোড হচ্ছে..." },
  "No data": { en: "No data available", bn: "কোন তথ্য নেই" },
  "All": { en: "All", bn: "সব" },

  // ===== Module Groups =====
  "Commercial": { en: "Commercial", bn: "কমার্শিয়াল" },
  "Supply Chain": { en: "Supply Chain", bn: "সাপ্লাই চেইন" },
  "Sales & Customers": { en: "Sales & Customers", bn: "সেলস ও কাস্টমার" },
  "Finance & Control": { en: "Finance & Control", bn: "ফাইন্যান্স ও কন্ট্রোল" },
  "Intelligence": { en: "Intelligence", bn: "ইন্টেলিজেন্স" },
  "Platform & Security": { en: "Platform & Security", bn: "প্ল্যাটফর্ম ও সিকিউরিটি" },

  // ===== Modules =====
  "Master Data": { en: "Master Data", bn: "মাস্টার ডেটা" },
  "Procurement": { en: "Procurement", bn: "প্রকিউরমেন্ট" },
  "Inventory": { en: "Inventory", bn: "ইনভেন্টরি" },
  "Warehouse Mgmt": { en: "Warehouse Mgmt", bn: "ওয়্যারহাউস" },
  "Sales Orders": { en: "Sales Orders", bn: "সেলস অর্ডার" },
  "Distribution Mgmt": { en: "Distribution Mgmt", bn: "ডিস্ট্রিবিউশন" },
  "Field Sales": { en: "Field Sales", bn: "ফিল্ড সেলস" },
  "CRM": { en: "CRM", bn: "সিআরএম" },
  "Delivery & Fleet": { en: "Delivery & Fleet", bn: "ডেলিভারি ও ফ্লিট" },
  "Returns": { en: "Returns", bn: "রিটার্ন" },
  "Finance & Accounting": { en: "Finance & Accounting", bn: "ফাইন্যান্স ও অ্যাকাউন্টিং" },
  "VAT & Tax": { en: "VAT & Tax", bn: "ভ্যাট ও ট্যাক্স" },
  "Credit Control": { en: "Credit Control", bn: "ক্রেডিট কন্ট্রোল" },
  "HR & Payroll": { en: "HR & Payroll", bn: "এইচআর ও পেরোল" },
  "Workflow & Approvals": { en: "Workflow & Approvals", bn: "ওয়ার্কফ্লো ও অ্যাপ্রুভাল" },
  "BI Dashboard": { en: "BI Dashboard", bn: "বিআই ড্যাশবোর্ড" },
  "AI Copilot": { en: "AI Copilot", bn: "এআই কোপাইলট" },
  "Budget & Costing": { en: "Budget & Costing", bn: "বাজেট ও কস্টিং" },
  "Marketing": { en: "Marketing", bn: "মার্কেটিং" },
  "B2B Portal": { en: "B2B Portal", bn: "বিটুবি পোর্টাল" },
  "Payment & Banking": { en: "Payment & Banking", bn: "পেমেন্ট ও ব্যাংকিং" },
  "Support Desk": { en: "Support Desk", bn: "সাপোর্ট ডেস্ক" },
  "Documents": { en: "Documents", bn: "ডকুমেন্ট" },
  "Security & Audit": { en: "Security & Audit", bn: "সিকিউরিটি ও অডিট" },
  "System Admin": { en: "System Admin", bn: "সিস্টেম অ্যাডমিন" },
  "API & Integration": { en: "API & Integration", bn: "এপিআই ও ইন্টিগ্রেশন" },
  "Mobile Apps": { en: "Mobile Apps", bn: "মোবাইল অ্যাপ" },
  "Quality & Trace": { en: "Quality & Trace", bn: "কোয়ালিটি ও ট্রেস" },
  "Industry Modules": { en: "Industry Modules", bn: "ইন্ডাস্ট্রি মডিউল" },

  // ===== Common Labels =====
  "Supplier": { en: "Supplier", bn: "সাপ্লায়ার" },
  "Customer": { en: "Customer", bn: "কাস্টমার" },
  "Product": { en: "Product", bn: "পণ্য" },
  "Warehouse": { en: "Warehouse", bn: "ওয়্যারহাউস" },
  "Employee": { en: "Employee", bn: "কর্মী" },
  "Purchase Order": { en: "Purchase Order", bn: "পারচেজ অর্ডার" },
  "Sales Order": { en: "Sales Order", bn: "সেলস অর্ডার" },
  "Invoice": { en: "Invoice", bn: "ইনভয়েস" },
  "Receipt": { en: "Receipt", bn: "রসিদ" },
  "Journal Entry": { en: "Journal Entry", bn: "জার্নাল এন্ট্রি" },
  "Approve": { en: "Approve", bn: "অনুমোদন" },
  "Reject": { en: "Reject", bn: "প্রত্যাখ্যান" },
  "Receive": { en: "Receive", bn: "গ্রহণ" },
  "Pick": { en: "Pick", bn: "পিক" },
  "Dispatch": { en: "Dispatch", bn: "ডিসপ্যাচ" },
  "Deliver": { en: "Deliver", bn: "ডেলিভার" },
  "Post": { en: "Post", bn: "পোস্ট" },
  "Stock": { en: "Stock", bn: "স্টক" },
  "Price": { en: "Price", bn: "মূল্য" },
  "Cost": { en: "Cost", bn: "খরচ" },
  "Margin": { en: "Margin", bn: "মার্জিন" },
  "Outstanding": { en: "Outstanding", bn: "বকেয়া" },
  "Credit Limit": { en: "Credit Limit", bn: "ক্রেডিট লিমিট" },
  "Payment": { en: "Payment", bn: "পেমেন্ট" },
  "VAT": { en: "VAT", bn: "ভ্যাট" },
  "Tax": { en: "Tax", bn: "ট্যাক্স" },
  "Reports": { en: "Reports", bn: "রিপোর্ট" },
  "Dashboard": { en: "Dashboard", bn: "ড্যাশবোর্ড" },
  "Settings": { en: "Settings", bn: "সেটিংস" },
  "Backup": { en: "Backup", bn: "ব্যাকআপ" },
  "Restore": { en: "Restore", bn: "রিস্টোর" },
  "Download": { en: "Download", bn: "ডাউনলোড" },
  "Upload": { en: "Upload", bn: "আপলোড" },
  "Sign": { en: "Sign", bn: "স্বাক্ষর" },
  "Create": { en: "Create", bn: "তৈরি" },
  "Update": { en: "Update", bn: "আপডেট" },
  "Notes": { en: "Notes", bn: "নোট" },
  "Description": { en: "Description", bn: "বিবরণ" },
  "Category": { en: "Category", bn: "ক্যাটাগরি" },
  "Brand": { en: "Brand", bn: "ব্র্যান্ড" },
  "Code": { en: "Code", bn: "কোড" },
  "Name": { en: "Name", bn: "নাম" },
  "Phone": { en: "Phone", bn: "ফোন" },
  "Email": { en: "Email", bn: "ইমেইল" },
  "Address": { en: "Address", bn: "ঠিকানা" },
  "Zone": { en: "Zone", bn: "জোন" },
  "Branch": { en: "Branch", bn: "ব্রাঞ্চ" },
  "Department": { en: "Department", bn: "বিভাগ" },
  "Designation": { en: "Designation", bn: "পদবি" },
  "Vehicle": { en: "Vehicle", bn: "যানবাহন" },
  "Driver": { en: "Driver", bn: "চালক" },
  "Trip": { en: "Trip", bn: "ট্রিপ" },
  "Route": { en: "Route", bn: "রুট" },
  "Batch": { en: "Batch", bn: "ব্যাচ" },
  "Expiry": { en: "Expiry", bn: "মেয়াদ" },
  "Unit Price": { en: "Unit Price", bn: "একক মূল্য" },
  "Discount": { en: "Discount", bn: "ডিসকাউন্ট" },
  "Subtotal": { en: "Subtotal", bn: "সাবটোটাল" },
  "Grand Total": { en: "Grand Total", bn: "মোট" },
  "Pending": { en: "Pending", bn: "বিচারাধীন" },
  "Approved": { en: "Approved", bn: "অনুমোদিত" },
  "Rejected": { en: "Rejected", bn: "প্রত্যাখ্যাত" },
  "Received": { en: "Received", bn: "গৃহীত" },
  "Delivered": { en: "Delivered", bn: "ডেলিভার্ড" },
  "Posted": { en: "Posted", bn: "পোস্টেড" },
  "Paid": { en: "Paid", bn: "পরিশোধিত" },
  "Active": { en: "Active", bn: "সক্রিয়" },
  "Inactive": { en: "Inactive", bn: "নিষ্ক্রিয়" },
  "Collapsed": { en: "Collapse Sidebar", bn: "সাইডবার বন্ধ" },
  "System Online": { en: "System Online", bn: "সিস্টেম অনলাইন" },
  "Audit Active": { en: "Audit Active", bn: "অডিট সক্রিয়" },
  "Reset": { en: "Reset", bn: "রিসেট" },
  "Language": { en: "Language", bn: "ভাষা" },
  "English": { en: "English", bn: "ইংরেজি" },
  "Bangla": { en: "Bangla", bn: "বাংলা" },

  // ===== Page Labels =====
  "Product List": { en: "Product List", bn: "পণ্য তালিকা" },
  "New Product": { en: "New Product", bn: "নতুন পণ্য" },
  "Customer List": { en: "Customer List", bn: "কাস্টমার তালিকা" },
  "New Customer": { en: "New Customer", bn: "নতুন কাস্টমার" },
  "Supplier List": { en: "Supplier List", bn: "সাপ্লায়ার তালিকা" },
  "New Supplier": { en: "New Supplier", bn: "নতুন সাপ্লায়ার" },
  "Warehouse List": { en: "Warehouse List", bn: "ওয়্যারহাউস তালিকা" },
  "New Warehouse": { en: "New Warehouse", bn: "নতুন ওয়্যারহাউস" },
  "All POs": { en: "All POs", bn: "সব পিও" },
  "Create PO": { en: "Create PO", bn: "পিও তৈরি" },
  "All GRNs": { en: "All GRNs", bn: "সব জিআরএন" },
  "All SOs": { en: "All SOs", bn: "সব এসও" },
  "Create SO": { en: "Create SO", bn: "এসও তৈরি" },
  "All Invoices": { en: "All Invoices", bn: "সব ইনভয়েস" },
  "All Receipts": { en: "All Receipts", bn: "সব রসিদ" },
  "All RMAs": { en: "All RMAs", bn: "সব আরএমএ" },
  "New RMA": { en: "New RMA", bn: "নতুন আরএমএ" },
  "Journal Entries": { en: "Journal Entries", bn: "জার্নাল এন্ট্রি" },
  "Trial Balance": { en: "Trial Balance", bn: "ট্রায়াল ব্যালেন্স" },
  "AR Aging": { en: "AR Aging", bn: "এআর এজিং" },
  "AP Ledger": { en: "AP Ledger", bn: "এপি লেজার" },
  "VAT Ledger": { en: "VAT Ledger", bn: "ভ্যাট লেজার" },
  "VAT Return": { en: "VAT Return", bn: "ভ্যাট রিটার্ন" },
  "Credit Scorecard": { en: "Credit Scorecard", bn: "ক্রেডিট স্কোরকার্ড" },
  "Employee List": { en: "Employee List", bn: "কর্মী তালিকা" },
  "Pending Approvals": { en: "Pending Approvals", bn: "বিচারাধীন অনুমোদন" },
  "History": { en: "History", bn: "ইতিহাস" },
  "Live Map": { en: "Live Map", bn: "লাইভ ম্যাপ" },
  "Salesman Tracking": { en: "Salesman Tracking", bn: "সেলসম্যান ট্র্যাকিং" },
  "Realtime Orders": { en: "Realtime Orders", bn: "রিয়েলটাইম অর্ডার" },
  "Zone Management": { en: "Zone Management", bn: "জোন ম্যানেজমেন্ট" },
  "Team Chat": { en: "Team Chat", bn: "টিম চ্যাট" },
  "System Settings": { en: "System Settings", bn: "সিস্টেম সেটিংস" },
  "Data Management": { en: "Data Management", bn: "ডেটা ম্যানেজমেন্ট" },
  "Backup & Restore": { en: "Backup & Restore", bn: "ব্যাকআপ ও রিস্টোর" },
  "Retail POS": { en: "Retail POS", bn: "রিটেইল পিওএস" },
  "E-Commerce": { en: "E-Commerce", bn: "ই-কমার্স" },
  "Manufacturing": { en: "Manufacturing", bn: "ম্যানুফ্যাকচারিং" },
  "Project Sales": { en: "Project Sales", bn: "প্রজেক্ট সেলস" },
  "3PL Management": { en: "3PL Management", bn: "থ্রিপিএল ম্যানেজমেন্ট" },
  "Consignment": { en: "Consignment", bn: "কনসাইনমেন্ট" },
  "ESG & Sustainability": { en: "ESG & Sustainability", bn: "ইএসজি ও সাসটেইনাবিলিটি" },
  "Stock by Product": { en: "Stock by Product", bn: "পণ্য অনুযায়ী স্টক" },
  "Stock Ledger": { en: "Stock Ledger", bn: "স্টক লেজার" },
  "Warehouse Stock": { en: "Warehouse Stock", bn: "ওয়্যারহাউস স্টক" },
  "Reorder List": { en: "Reorder List", bn: "রিঅর্ডার তালিকা" },
  "P&L Statement": { en: "P&L Statement", bn: "লাভ-ক্ষতি বিবরণী" },
  "Balance Sheet": { en: "Balance Sheet", bn: "ব্যালেন্স শিট" },
  "Cash Flow": { en: "Cash Flow", bn: "ক্যাশ ফ্লো" },
  "Stock Aging": { en: "Stock Aging", bn: "স্টক এজিং" },
  "Stock Valuation": { en: "Stock Valuation", bn: "স্টক ভ্যালুয়েশন" },
  "Sales Summary": { en: "Sales Summary", bn: "সেলস সারাংশ" },
  "Margin Analysis": { en: "Margin Analysis", bn: "মার্জিন বিশ্লেষণ" },
  "Supplier Performance": { en: "Supplier Performance", bn: "সাপ্লায়ার পারফরম্যান্স" },
  "Roles & Permissions": { en: "Roles & Permissions", bn: "রোল ও পারমিশন" },
  "Audit Trail": { en: "Audit Trail", bn: "অডিট ট্রেইল" },
  "Company Setup": { en: "Company Setup", bn: "কোম্পানি সেটআপ" },
  "API Catalogue": { en: "API Catalogue", bn: "এপিআই ক্যাটালগ" },
  "Device Status": { en: "Device Status", bn: "ডিভাইস স্ট্যাটাস" },
  "Inspection Records": { en: "Inspection Records", bn: "ইন্সপেকশন রেকর্ড" },
  "Module Status": { en: "Module Status", bn: "মডিউল স্ট্যাটাস" },
  "Distributor Scorecard": { en: "Distributor Scorecard", bn: "ডিস্ট্রিবিউটর স্কোরকার্ড" },
  "Field Visits": { en: "Field Visits", bn: "ফিল্ড ভিজিট" },
  "Beat Plans": { en: "Beat Plans", bn: "বিট প্ল্যান" },
  "Customer 360": { en: "Customer 360", bn: "কাস্টমার ৩৬০" },
  "Support Tickets": { en: "Support Tickets", bn: "সাপোর্ট টিকেট" },
  "Active Trips": { en: "Active Trips", bn: "সক্রিয় ট্রিপ" },
  "Fleet Master": { en: "Fleet Master", bn: "ফ্লিট মাস্টার" },
  "Budget vs Actual": { en: "Budget vs Actual", bn: "বাজেট বনাম বাস্তব" },
  "Active Campaigns": { en: "Active Campaigns", bn: "সক্রিয় ক্যাম্পেইন" },
  "Portal Orders": { en: "Portal Orders", bn: "পোর্টাল অর্ডার" },
  "Collection Register": { en: "Collection Register", bn: "কালেকশন রেজিস্টার" },
  "Repository": { en: "Repository", bn: "রিপোজিটরি" },
};

interface I18nState {
  lang: Language;
  setLang: (lang: Language) => void;
  toggle: () => void;
  t: (key: string) => string;
}

export const useI18n = create<I18nState>()(
  persist(
    (set, get) => ({
      lang: "en",
      setLang: (lang) => set({ lang }),
      toggle: () => set((s) => ({ lang: s.lang === "en" ? "bn" : "en" })),
      t: (key: string) => {
        const entry = translations[key];
        if (!entry) return key;
        return entry[get().lang] || key;
      },
    }),
    {
      name: "omega-i18n",
      storage: createJSONStorage(() => (typeof window !== "undefined" ? window.localStorage : (undefined as unknown as Storage))),
    }
  )
);
